import React from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Stack,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import GitHubIcon from '@mui/icons-material/GitHub';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import config from '../config.json';

const socialIconMap = {
  GitHub: <GitHubIcon fontSize="small" />,
  Twitter: <TwitterIcon fontSize="small" />,
  LinkedIn: <LinkedInIcon fontSize="small" />,
};

function Footer() {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        py: 6,
        borderTop: `1px solid ${theme.custom.subtleBorder}`,
        background: theme.custom.footerBg,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 3,
            mb: 4,
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CodeIcon
              sx={{
                fontSize: 28,
                color: 'primary.main',
                mr: 1,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                background: theme.custom.gradientPrimary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: '1.3rem',
              }}
            >
              {config.company.name}
            </Typography>
          </Box>

          {/* Links */}
          <Stack
            direction="row"
            spacing={3}
            sx={{
              '& a': {
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'color 0.2s',
                '&:hover': {
                  color: 'primary.main',
                },
              },
            }}
          >
            {config.footer.links.map((link) => (
              <a
                key={link.label}
                href={link.external ? config.urls.githubSupport : link.href}
                {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {link.label}
              </a>
            ))}
          </Stack>

          {/* Socials */}
          <Stack direction="row" spacing={1}>
            {config.footer.social.map((item) => (
              <IconButton
                key={item.icon}
                size="small"
                {...(item.url ? { href: item.url, target: '_blank', rel: 'noopener noreferrer' } : {})}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: theme.custom.hoverBg,
                  },
                }}
              >
                {socialIconMap[item.icon]}
              </IconButton>
            ))}
          </Stack>
        </Box>

        <Divider
          sx={{ borderColor: theme.custom.divider, mb: 3 }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
          >
            © {currentYear} {config.company.name}. {config.company.copyright}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              '& a': {
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.8rem',
                '&:hover': { color: 'primary.main' },
              },
            }}
          >
            {config.footer.legal.map((link) => (
              <a key={link.label} href={link.href}>{link.label}</a>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
